import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTheme, ThemeColors } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Z_INDEX, zStyle } from '../../../lib/zLayers';
import Card from '../../../components/ui/Card';
import { SegmentedControl } from '../../../components/ui';
import { moduleConfig } from './config';
import { useStoreItemHistory, ItemHistoryEvent, ItemHistoryPort } from './useData';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';

/** One tab per supply stage (matches API `kind`). */
const STAGES: { id: ItemHistoryEvent['kind']; label: string; short: string; accent: string; activeRing: string }[] = [
  { id: 'requisition', label: 'Requested', short: 'Req', accent: 'text-slate-600 dark:text-slate-300', activeRing: 'ring-slate-400/40' },
  { id: 'enquiry', label: 'Enquired', short: 'Enq', accent: 'text-blue-600 dark:text-blue-400', activeRing: 'ring-blue-400/50' },
  { id: 'quotation', label: 'Quoted', short: 'Quote', accent: 'text-amber-600 dark:text-amber-400', activeRing: 'ring-amber-400/50' },
  { id: 'purchase_order', label: 'Ordered', short: 'PO', accent: 'text-emerald-600 dark:text-emerald-400', activeRing: 'ring-emerald-400/50' },
  { id: 'delivery', label: 'Delivered', short: 'Dlv', accent: 'text-cyan-600 dark:text-cyan-400', activeRing: 'ring-cyan-400/50' },
  { id: 'invoice', label: 'Invoiced', short: 'Inv', accent: 'text-violet-600 dark:text-violet-400', activeRing: 'ring-violet-400/50' },
];

const KIND_LABEL: Record<ItemHistoryEvent['kind'], string> = {
  requisition: 'Requisition',
  enquiry: 'Enquiry',
  quotation: 'Quotation',
  purchase_order: 'Purchase order',
  delivery: 'Delivery',
  invoice: 'Invoice',
};

function sortByDateDesc(rows: ItemHistoryEvent[]): ItemHistoryEvent[] {
  return [...rows].sort((a, b) => {
    if (a.at !== b.at) {
      return a.at < b.at ? 1 : -1;
    }
    return b.documentId - a.documentId;
  });
}

function formatPort(p: ItemHistoryPort | null): string {
  if (!p) return '';
  const main = [p.name, p.countryName].filter((x) => (x ?? '').toString().trim() !== '');
  const base = main.join(' · ');
  if (p.code) {
    return base ? `${base} (${p.code})` : p.code;
  }
  return base || '';
}

function formatMoney(
  n: number | null | undefined,
  currency: string | null,
  fracMin = 2,
  fracMax = 4,
): string {
  if (n == null) return '';
  const s = n.toLocaleString(undefined, { minimumFractionDigits: fracMin, maximumFractionDigits: fracMax });
  return currency ? `${currency} ${s}` : s;
}

const HistoryTable: React.FC<{
  rows: ItemHistoryEvent[];
  showTypeColumn: boolean;
  colors: ThemeColors;
}> = ({ rows, showTypeColumn, colors }) => (
  <div
    className={cn(
      'overflow-hidden rounded-xl',
      colors.border,
      'shadow-sm',
      'ring-1 ring-inset ring-slate-200/70 dark:ring-white/5',
    )}
  >
    <div className={cn('overflow-x-auto', colors.surface, 'bg-white/90 dark:bg-slate-800/40')}>
      <table className="w-full min-w-[48rem] border-collapse text-left text-sm">
        <thead>
          <tr
            className={cn(
              'border-b text-left text-xs font-semibold tracking-wide',
              colors.border,
              'bg-slate-50/90 text-slate-600 dark:bg-slate-800/80 dark:text-slate-300',
            )}
          >
            <th className="whitespace-nowrap px-4 py-3.5 first:pl-4 sm:pl-5">Date</th>
            {showTypeColumn && <th className="px-2 py-3.5 sm:px-3">Type</th>}
            <th className="min-w-[5.5rem] px-2 py-3.5 sm:px-3">Document</th>
            <th className="min-w-[5.5rem] px-2 py-3.5 sm:px-3">Case</th>
            <th className="min-w-[9rem] px-2 py-3.5 sm:px-3">Port / place</th>
            <th className="min-w-[7rem] px-2 py-3.5 sm:px-3">Supplier</th>
            <th className="w-20 px-2 py-3.5 text-right sm:px-3">Qty</th>
            <th className="w-28 px-2 py-3.5 text-right sm:px-3">Unit</th>
            <th className="w-28 px-2 py-3.5 pr-3 text-right sm:px-3 sm:pr-4">Line total</th>
            <th className="w-24 pr-3 text-right sm:pr-4"> </th>
          </tr>
        </thead>
        <tbody className={cn('divide-y', colors.divide)}>
          {rows.map((row, i) => (
            <tr
              key={`${row.kind}-${row.documentId}-${row.at}-${i}`}
              className={cn(
                'align-top transition-colors',
                'hover:bg-indigo-50/40 dark:hover:bg-indigo-950/20',
                i % 2 === 0 && 'bg-slate-50/30 dark:bg-slate-900/20',
              )}
            >
              <td
                className={cn('whitespace-nowrap px-4 py-3.5 tabular-nums', colors.text, 'sm:pl-5', 'font-medium')}
              >
                {row.at}
              </td>
              {showTypeColumn && <td className={cn('px-2 py-3.5 sm:px-3', colors.textSecondary)}>{KIND_LABEL[row.kind] ?? row.kind}</td>}
              <td className={cn('px-2 py-3.5 sm:px-3', colors.text, 'max-w-[10rem] sm:max-w-xs')}>
                <span className="font-mono text-xs sm:text-sm">{(row.documentRef ?? '').trim() || `#${row.documentId}`}</span>
              </td>
              <td className={cn('px-2 py-3.5 sm:px-3', colors.textSecondary)}>{(row.caseRef ?? '').trim() || (row.caseId != null ? `#${row.caseId}` : '')}</td>
              <td
                className={cn('max-w-xs px-2 py-3.5 break-words sm:px-3', colors.text)}
                title={formatPort(row.port)}
              >
                {formatPort(row.port)}
              </td>
              <td
                className={cn('max-w-[12rem] px-2 py-3.5 sm:max-w-xs sm:px-3', colors.text)}
                title={row.supplier?.name ?? ''}
              >
                {row.supplier
                  ? [row.supplier.name, row.supplier.code ? `(${row.supplier.code})` : null].filter(Boolean).join(' ')
                  : ''}
              </td>
              <td className={cn('px-2 py-3.5 text-right tabular-nums sm:px-3', colors.textSecondary)}>{row.qty ?? ''}</td>
              <td className={cn('px-2 py-3.5 text-right tabular-nums', colors.text)}>{formatMoney(row.unitPrice, row.currencyCode)}</td>
              <td className={cn('px-2 py-3.5 pr-3 text-right tabular-nums sm:px-3', colors.text, 'sm:pr-4')}>
                {formatMoney(row.lineTotal, row.currencyCode, 2, 2)}
              </td>
              <td className="px-2 py-3.5 pr-3 text-right sm:pr-4">
                {row.href ? (
                  <Link
                    to={row.href}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors',
                      colors.border,
                      colors.interactiveHover,
                      colors.text,
                      colors.primaryFg,
                    )}
                    title="Open document"
                  >
                    <span className="hidden sm:inline">Open</span>
                    <svg className="h-4 w-4 shrink-0 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </Link>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export const ItemHistoryLink: React.FC<{
  itemId: number;
  className?: string;
  children?: React.ReactNode;
  title?: string;
}> = ({ itemId, className, children, title = 'Item supply history' }) => (
  <Link
    to={moduleConfig.module.route.history(itemId)}
    className={cn('inline-flex items-center justify-center rounded-md p-1', className)}
    onClick={(e) => e.stopPropagation()}
    title={title}
  >
    {children ?? (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )}
  </Link>
);

export function ItemHistoryContent({ itemId, onClose }: { itemId: number; onClose: () => void }) {
  const { colors } = useTheme();
  const { data, isLoading, isError } = useStoreItemHistory(itemId);
  const events = data?.events ?? [];
  const item = data?.item;

  const eventsByStage = useMemo(() => {
    const map = new Map<ItemHistoryEvent['kind'], ItemHistoryEvent[]>();
    for (const s of STAGES) {
      map.set(s.id, []);
    }
    for (const e of events) {
      const arr = map.get(e.kind);
      if (arr) arr.push(e);
    }
    for (const s of STAGES) {
      map.set(s.id, sortByDateDesc(map.get(s.id)!));
    }
    return map;
  }, [events]);

  const [activeTab, setActiveTab] = useState<string>(STAGES[0].id);
  const tabPrimedRef = useRef(false);
  useEffect(() => {
    tabPrimedRef.current = false;
  }, [itemId]);
  useEffect(() => {
    if (tabPrimedRef.current || events.length === 0) return;
    for (const s of STAGES) {
      if (events.some((e) => e.kind === s.id)) {
        setActiveTab(s.id);
        break;
      }
    }
    tabPrimedRef.current = true;
  }, [events]);

  const activeMeta = useMemo(() => STAGES.find((s) => s.id === activeTab) ?? STAGES[0], [activeTab]);
  const activeRows = eventsByStage.get(activeTab as ItemHistoryEvent['kind']) ?? [];
  const totalInStage = activeRows.length;

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-12">
        <LoadingSpinner size="large" message="Loading history…" />
      </div>
    );
  }

  if (isError || !item) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-8 text-center">
        <p className={cn('text-sm', colors.text)}>Could not load item history.</p>
        <Link
          to={moduleConfig.module.route.list}
          className={cn('text-sm font-medium', colors.primaryFg, 'hover:underline')}
        >
          Back to items
        </Link>
        <button
          type="button"
          onClick={onClose}
          className={cn('rounded-lg border px-3 py-1.5 text-sm', colors.border, colors.interactiveHover, colors.text)}
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <div className={cn('flex h-full min-h-0 flex-1 flex-col', colors.surface)}>
      <Card
        className="flex h-full min-h-0 flex-1 flex-col overflow-hidden rounded-none border-0 p-0 shadow-none ring-0"
        variant="default"
      >
        <div
          className={cn(
            'shrink-0 border-b bg-gradient-to-br from-slate-50 via-white to-indigo-50/40 px-4 py-4 dark:from-slate-800/50 dark:via-slate-800/80 dark:to-indigo-950/20',
            colors.border,
          )}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 gap-3 sm:gap-4">
              <div
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-600 shadow-sm dark:bg-indigo-500/20 dark:text-indigo-300"
                aria-hidden
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="min-w-0 pr-1">
                <p className={cn('text-xs font-medium uppercase tracking-wider', colors.textMuted)}>Supply history</p>
                <h1 className={cn('mt-1 line-clamp-2 text-lg font-bold tracking-tight sm:text-xl', colors.text)} title={item.name}>
                  {item.name}
                </h1>
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  {item.vendorCode ? (
                    <span
                      className={cn(
                        'inline-flex items-center rounded-lg border px-2.5 py-0.5 font-mono text-xs',
                        colors.border,
                        colors.textSecondary,
                      )}
                    >
                      {item.vendorCode}
                    </span>
                  ) : null}
                  {events.length > 0 && (
                    <span className={cn('text-xs', colors.textMuted)}>{events.length} event{events.length === 1 ? '' : 's'}</span>
                  )}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className={cn(
                'shrink-0 rounded-xl p-2.5 transition-colors',
                colors.textSecondary,
                colors.interactiveHover,
                'text-slate-500 hover:bg-slate-200/80 hover:text-slate-800 dark:hover:bg-slate-600/80 dark:hover:text-slate-100',
              )}
              aria-label="Close supply history"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {events.length === 0 ? (
            <div className="px-4 py-10 text-center sm:px-6">
              <div
                className={cn('mx-auto max-w-sm rounded-2xl border-2 border-dashed px-5 py-8', colors.border)}
              >
                <div className="mx-auto mb-2 flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700/80">
                  <svg className="h-4 w-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className={cn('text-sm font-medium', colors.text)}>No activity yet</p>
                <p className={cn('mt-1 text-sm', colors.textSecondary)}>This item has not appeared on requisitions, orders, or other supply documents.</p>
              </div>
            </div>
          ) : (
            <div className="px-3 py-3 sm:px-4 sm:py-4">
              <p className={cn('mb-2.5 text-xs', colors.textMuted)}>Newest first within each stage.</p>
              <SegmentedControl
                className="mb-3 w-full shadow-inner"
                fullWidth
                size="sm"
                aria-label="Stage"
                value={activeTab}
                onChange={setActiveTab}
                options={STAGES.map((s) => {
                  const n = eventsByStage.get(s.id)?.length ?? 0;
                  return {
                    id: s.id,
                    label: s.short,
                    count: n,
                    disabled: n === 0,
                  };
                })}
              />

              {totalInStage === 0 ? (
                <p className={cn('rounded-lg border border-dashed px-3 py-5 text-center text-sm', colors.border, colors.textSecondary)}>
                  No <span className="lowercase">{activeMeta.label}</span> events for this item. Choose another stage above.
                </p>
              ) : (
                <div>
                  <div className="mb-2.5 flex justify-end">
                    <span className="text-xs tabular-nums text-slate-500 dark:text-slate-400">
                      {totalInStage} row{totalInStage === 1 ? '' : 's'}
                    </span>
                  </div>
                  <HistoryTable rows={activeRows} showTypeColumn={false} colors={colors} />
                </div>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

function ItemHistoryDrawer({ open, itemId, onClose }: { open: boolean; itemId: number; onClose: () => void }) {
  const { colors } = useTheme();
  const [entered, setEntered] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open) {
      setEntered(false);
      return;
    }
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <>
      <div
        role="presentation"
        className="fixed inset-0 cursor-default bg-slate-900/40 transition-opacity dark:bg-black/50"
        style={zStyle(Z_INDEX.itemHistoryDrawerBackdrop)}
        aria-hidden
        onClick={onClose}
      />
      <div
        ref={panelRef}
        className={cn(
          'fixed right-0 top-0 flex h-[100dvh] min-h-0 w-full max-w-[min(100vw,64rem)] flex-col border-l shadow-2xl',
          colors.border,
          colors.surface,
          'will-change-transform',
          entered ? 'translate-x-0' : 'translate-x-full',
          'transform transition-transform duration-300 ease-out',
        )}
        style={zStyle(Z_INDEX.itemHistoryDrawer)}
        role="dialog"
        aria-modal="true"
        aria-label="Item supply history"
        onClick={(e) => e.stopPropagation()}
      >
        <ItemHistoryContent itemId={itemId} onClose={onClose} />
      </div>
    </>,
    document.body,
  );
}

const ItemHistory: React.FC = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const id = idParam && /^\d+$/.test(idParam) ? parseInt(idParam, 10) : NaN;

  const handleClose = useCallback(() => {
    navigate(moduleConfig.module.route.detail(id), { replace: true });
  }, [navigate, id]);

  if (!Number.isFinite(id) || id <= 0) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <Card className="p-8 text-center">
          <p className={cn('text-sm', colors.text)}>Invalid item id.</p>
        </Card>
      </div>
    );
  }

  return <ItemHistoryDrawer open itemId={id} onClose={handleClose} />;
};

export default ItemHistory;
